/**
 * Build checkout metadata for payment tracking
 * Captures UTM parameters, campaign info, and AB test data
 */

import { getAllExperiments } from '../app/statsig';
import { getActiveExperiments } from '../config/experiments';
import { getWebId } from './web-id';
import { hasAnalyticsConsent } from './consent';
import { getPendingPurchase } from './purchase-tracking';
import { store } from '../app/store';
import { FLOW_CONFIG } from '../config/flow';

// Cache URL params since they don't change during session
let cachedUrlParams: Record<string, string> | null = null;

/**
 * Read a cookie value by name
 */
function getCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Get URL parameters (cached)
 */
function getUrlParams(): Record<string, string> {
  if (cachedUrlParams) return cachedUrlParams;

  const urlParams = new URLSearchParams(window.location.search);
  cachedUrlParams = {
    network: urlParams.get('network') || 'organic',
    campaign_name: urlParams.get('campaign_name') || urlParams.get('utm_campaign') || '',
    adset_name: urlParams.get('adset_name') || urlParams.get('utm_medium') || '',
    ad_name: urlParams.get('ad_name') || urlParams.get('utm_content') || '',
    campaign_id: urlParams.get('campaign_id') || '',
    adgroup_id: urlParams.get('adgroup_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    utm_source: urlParams.get('utm_source') || '',
    utm_term: urlParams.get('utm_term') || '',
    fbclid: urlParams.get('fbclid') || '',
    gclid: urlParams.get('gclid') || '',
    ttclid: urlParams.get('ttclid') || '',
  };

  return cachedUrlParams;
}

/**
 * Build complete checkout metadata
 */
export function buildCheckoutMetadata(): Record<string, string> {
  const urlParams = getUrlParams();
  const experiments = getAllExperiments();

  // Filter out empty values
  const metadata: Record<string, string> = {};

  // Add URL params
  for (const [key, value] of Object.entries(urlParams)) {
    if (value) {
      metadata[key] = value;
    }
  }

  // Add all exposed experiments (already prefixed with "experiment_")
  for (const [key, value] of Object.entries(experiments)) {
    if (value && value !== 'control') {
      metadata[key] = value;
    }
  }

  // Local client-assigned A/B tests (same experiment_ prefix convention).
  // Both arms are recorded — the control arm's name is meaningful here.
  for (const [name, variant] of Object.entries(getActiveExperiments())) {
    metadata[`experiment_${name}`] = variant;
  }

  // Add web identity (our owned web_id, also pushed into Statsig as stableID).
  // Analytics-consent gated: getWebId() mints a persistent localStorage id and
  // the backend keys its Statsig/Amplitude identity stitch on it — a user who
  // declined analytics must not get either (purchase itself works without it).
  if (hasAnalyticsConsent()) {
    const webId = getWebId();
    if (webId) {
      metadata.web_id = webId;
      metadata.statsig_stable_id = webId;
    }
  }

  // Add age group from quiz answers
  const ageRange = store.get<string>('wq_age_range');
  if (ageRange) {
    metadata.age_group = ageRange;
  }

  // Add all quiz answers (dataFields prefixed with wq_)
  for (const screen of FLOW_CONFIG.getAllScreens()) {
    const field = (screen as { dataField?: string }).dataField;
    if (!field || !field.startsWith('wq_')) continue;
    const value = store.get<string | string[]>(field);
    if (!value) continue;
    metadata[field] = Array.isArray(value) ? JSON.stringify(value) : String(value);
  }

  return metadata;
}

/**
 * Merge Meta (Facebook) tracking fields into checkout metadata:
 * - `fbp`/`fbc` cookies for Conversions-API match quality (the cookies only
 *   exist after the Pixel initialized, i.e. post-marketing-consent — implicit
 *   consent gating, mirroring how `fbclid` already rides along above).
 * - `meta_event_id` — the browser/server dedup key the worker's CAPI relay
 *   reads (`otherMetadata.meta_event_id`).
 *
 * Deliberately NOT part of buildCheckoutMetadata(): that runs in
 * screenController when the checkout button is clicked, BEFORE
 * beginPurchaseTracking() mints the event id in buildProviderCheckoutOptions.
 * Call this at setup-intent time, after beginPurchaseTracking().
 */
export function appendMetaTracking(metadata?: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = { ...metadata };

  const fbp = getCookie('_fbp');
  if (fbp) {
    result.fbp = fbp;
  }

  const fbc = getCookie('_fbc');
  if (fbc) {
    result.fbc = fbc;
  }

  const pending = getPendingPurchase();
  if (pending) {
    result.meta_event_id = pending.eventId;
  }

  return result;
}
